/**
 * Botón «Desactivar» / «Activar» con confirmación (auditoría de frontend FE-06): desactivar saca algo de la operación
 * del día, así que antes de hacerlo una ventana dice qué deja de pasar y pide confirmar. «Activar» sigue directo.
 * Nada se borra: lo desactivado queda en su lista, marcado, y se puede activar de nuevo.
 */
import { useState, type ReactNode } from 'react';
import { Button, type ButtonProps } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';

export interface BotonActivoProps {
  /** Estado de hoy: activo muestra «Desactivar» (con ventana); inactivo, «Activar» (directo). */
  activo: boolean;
  /** Título de la ventana, p. ej. «Desactivar Cemento gris». */
  titulo: string;
  /** Qué deja de pasar al desactivarlo, p. ej. «El producto deja de aparecer en el punto de venta…». */
  consecuencia: string;
  /**
   * Cambia el estado (`false` = desactivar). Si devuelve una promesa, la ventana espera a que termine y se cierra; el
   * error lo avisa quien llama (su toast), aquí solo se cierra la ventana.
   */
  alCambiar: (activo: boolean) => Promise<unknown> | void;
  cargando?: boolean | undefined;
  disabled?: boolean | undefined;
  variante?: ButtonProps['variant'];
  /** Variante del botón cuando dice «Activar» (si no viene, la misma). */
  varianteActivar?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  icono?: ReactNode;
  className?: string | undefined;
  testid?: string | undefined;
}

export function BotonActivo({ activo, titulo, consecuencia, alCambiar, cargando = false, disabled, variante = 'ghost', varianteActivar, size = 'sm', icono, className, testid }: BotonActivoProps) {
  const [abierto, setAbierto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const confirmar = async () => {
    setEnviando(true);
    try {
      await alCambiar(false);
    } catch {
      // El aviso del error ya lo dio quien llama (onError de su mutación)
    } finally {
      setEnviando(false);
      setAbierto(false);
    }
  };
  return (
    <>
      <Button
        size={size}
        variant={activo ? variante : (varianteActivar ?? variante)}
        className={className}
        disabled={disabled}
        cargando={cargando && !abierto}
        onClick={() => (activo ? setAbierto(true) : void alCambiar(true))}
        data-testid={testid}
      >
        {icono}
        {activo ? 'Desactivar' : 'Activar'}
      </Button>
      <Dialog open={abierto} onOpenChange={(o) => { if (!enviando) setAbierto(o); }}>
        <DialogContent data-testid={testid ? `${testid}-dialogo` : undefined}>
          <DialogHeader>
            <DialogTitle>{titulo}</DialogTitle>
            <DialogDescription>{consecuencia}</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Nada se borra: queda en su lista, marcado, y se puede activar de nuevo cuando haga falta.</p>
          <DialogFooter>
            <Button variant="outline" disabled={enviando} onClick={() => setAbierto(false)} data-testid={testid ? `${testid}-cancelar` : undefined}>Cancelar</Button>
            <Button variant="destructive" cargando={enviando} onClick={() => void confirmar()} data-testid={testid ? `${testid}-confirmar` : undefined}>Desactivar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
